import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Row } from "reactstrap";
import { useAuth } from "../../context/AuthContext";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { AssistancePartListDTO } from "../../models/AssistanceParts/AssistancePartListDTO";
import { PaginatedPartListDTO } from "../../models/AssistanceParts/PaginatedPartListDTO";
import { AssistanceService } from "../../services/AssistanceService";
import ButtonComponent from "../ButtonComponent";
import CheckboxComponent from "../CheckboxComponent";
import { useMobile } from "../Layout/UseResponsive";
import LoadingSpinner from "../LoadingSpinner";
import ModalComponent from "../ModalComponent";
import TableComponent from "../TableComponent";
import { UsedPartCard } from "./UsedPartCard";

type PartsUsedTableComponentType = {
    mode: "workshop" | "backoffice";
    assistanceId: number;
    reload: boolean;
    isComplete: boolean;
    isAcceptedBudget: boolean;
    assistanceStatus: string;
}

export function PartsUsedTableComponent({ mode, assistanceId, reload, isComplete, isAcceptedBudget, assistanceStatus }: PartsUsedTableComponentType) {
    //#region Variables
    const assistanceService = new AssistanceService();
    const [parts, setParts] = useState<AssistancePartListDTO[]>([]);
    const [removePart, setRemovedPart] = useState<AssistancePartListDTO>();
    const { preferedView } = useAuth()
    const { availableModules, pageSizeOptions } = useAvailableModulesContext();
    const isMobile = useMobile();
    const intl = useIntl();
    const defaultPageSizes: number[] = [5, 15, 30];

    // Usa o Utilities para obter os tamanhos de página com base nas configurações
    const pageSizes: number[] = Utilities.GetPageSizeFromSettings(defaultPageSizes, pageSizeOptions);

    // Usa o Utilities para obter o tamanho de página inicial
    const [pageSize, setPageSize] = useState<number>(
        Utilities.GetInitialPageSize(pageSizes, pageSizeOptions, defaultPageSizes[0])
    );

    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [canPreviousPage, setCanPreviousPage] = useState<boolean>(false);
    const [canNextPage, setCanNextPage] = useState<boolean>(false);
    const [goToPage, setGoToPage] = useState<number>(1);
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [reloaddAfterRemove, setReloadAfterRemove] = useState<boolean>(false);
    const [reloaddAfterUpdateBillable, setReloadAfterUpdateBillable] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({});
    const isFirstRender = useRef(true);
    const [isActiveProfabilityModule, setIsActiveProfabilityModule] = useState<boolean>(false);
    const isLocked = isAcceptedBudget || isComplete;
    //#endregion

    const toggleRow = (id: number) => {
        setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const fetchData = async (currentPage: number, pageSize: number) => {
        if (assistanceId === 0) {
            return;
        }
        setIsLoadingTable(true);

        var data: PaginatedPartListDTO = {
            currentPage: currentPage,
            pageSize: pageSize,
            assistanceId: assistanceId,
        }

        var response = await assistanceService.GetUsedParts(data);

        if (response.success === false) {
            setParts([]);
            setGoToPage(0);
            setIsLoadingTable(false);
            Toast.Show("error", response.message);
            return;
        }

        setCurrentPage(currentPage);
        setPageSize(pageSize);
        setParts(response.items);
        setTotalPages(response.totalPages);
        setIsLoadingTable(false);
        setCurrentPage(currentPage);
        setCanNextPage(response.totalPages > currentPage);
        setCanPreviousPage(currentPage > 1);
        setGoToPage(0);

        if (response.totalPages > 0 && currentPage > response.totalPages) {
            setGoToPage(1);
        }
    };

    const Remove = async (selectedPart: any) => {
        setIsLoadingTable(true);
        var response = await assistanceService.RemovePartFromAssistance(selectedPart.id);
        if (response.success === false) {
            setIsLoadingTable(false);
            Toast.Show("error", response.message);
            return
        }
        setIsLoadingTable(false);
        Toast.Show("success", "Peça removida da assistência com sucesso");
        setReloadAfterRemove(!reloaddAfterRemove);
    }

    const UpdateBillable = async (selectedPart: any, billable: boolean) => {
        setIsLoadingTable(true);
        var response = await assistanceService.SetBillableAssistancePart(selectedPart.id, billable);
        if (response.success === false) {
            setIsLoadingTable(false);
            Toast.Show("error", response.message);
            return;
        }
        setIsLoadingTable(false);
        Toast.Show("success", "Propriedade atualizada com sucesso");
        setReloadAfterUpdateBillable(!reloaddAfterUpdateBillable);
    }

    // modulo rentabilidade
    useEffect(() => {
        if (availableModules.includes("ProfitabilitySage50cIsActiveProfitabilityModule")) {
            setIsActiveProfabilityModule(true);
        }
    }, [availableModules]);


    const columns = React.useMemo(
        () => [
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.Code" />,
                accessor: "erpId",
                width: 5,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.Name" />,
                accessor: "name",
                width: 35,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.Quantity" />,
                accessor: "quantity",
                width: 5,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.Billable" />,
                accessor: "billable",
                show: isActiveProfabilityModule && preferedView === "Backoffice" && !isComplete,
                Cell: (props: any) => {
                    return (
                        preferedView === "Backoffice" && !isComplete ? (
                            <div style={{ display: "inline-flex", height: "30px" }}>
                                <CheckboxComponent
                                    checked={props.cell.row.original.billable}
                                    disabled={isComplete}
                                    onChange={(checked) => {
                                        setParts((prevParts) =>
                                            prevParts.map((prevPart) =>
                                                prevPart.id === props.cell.row.original.id
                                                    ? { ...prevPart, billable: checked }
                                                    : prevPart
                                            )
                                        );
                                    }}
                                />
                            </div>
                        ) : (
                            <div></div>
                        )
                    );
                },
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.PartDescription" />,
                accessor: "requestPartDescription",
                Cell: (props: any) => {
                    const isExpanded = expandedRows[props.cell.row.original.id];
                    return (
                        <div style={{ display: "inline-flex", height: "30px" }}>
                            <ButtonComponent
                                text={isExpanded ? intl.formatMessage({ id: "ExecutionsTableComponent.Close" }) : intl.formatMessage({ id: "ExecutionsTableComponent.Open" })}
                                onClick={() => toggleRow(props.cell.row.original.id)}
                            />
                        </div>
                    );
                },
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.UpdatePart" />,
                accessor: "  ",
                show: isActiveProfabilityModule && preferedView === "Backoffice" && !isComplete,
                Cell: (props: any) => {
                    if (preferedView === "Backoffice" && isComplete === false) {
                        return (
                            <div style={{ display: "inline-flex", height: "30px" }}>
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "PartsUsedTableComponent.UpdateBillable" })}
                                    onClick={() => {
                                        const currentPart = props.cell.row.original;
                                        const updatedBillable = currentPart.billable;
                                        UpdateBillable(currentPart, updatedBillable);
                                    }}
                                />
                            </div>
                        );
                    } else {
                        return <div></div>;
                    }
                },
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.RemovePart" />,
                accessor: " ",
                show: (assistanceStatus.toUpperCase() === 'EM CURSO') && !isLocked,
                Cell: (props: any) => {
                    if ((assistanceStatus.toUpperCase() === 'EM CURSO') && !isLocked) {
                        return (
                            <div style={{ display: "inline-flex", height: "30px" }}>
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "PartsUsedTableComponent.Remove" })}
                                    onClick={() => {
                                        setIsOpen(true);
                                        setRemovedPart(props.cell.row.original)
                                    }} />
                            </div>
                        )
                    }
                    else {
                        return <div></div>;
                    }
                },
                width: 10,
            },
        ],
        [isComplete, preferedView, isActiveProfabilityModule, expandedRows],
    );

    const getUsedPartsOrchestrator = async (currentPage: number, pageSize: number) => {
        if (assistanceId !== 0) {
            fetchData(currentPage, pageSize);
        }
    }

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (isMobile && assistanceId !== 0) {
                // On mobile, fetch data on initial render
                getUsedPartsOrchestrator(currentPage, pageSize);
            }
            return;
        }
        getUsedPartsOrchestrator(currentPage, pageSize);
    }, [currentPage, pageSize, reload, reloaddAfterRemove, assistanceId, reloaddAfterUpdateBillable]);

    const renderRowSubComponent = (row: any) => {
        const part = row.original;
        return (
            <tr>
                <td colSpan={columns.length}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <div style={{ display: "flex" }}>
                            <div style={{ width: "100%", padding: "0.5rem", textAlign: "left" }}>
                                <p style={{ marginBottom: "8px" }}><strong>Observações:</strong></p>
                                {part.requestPartDescription}
                            </div>
                        </div>
                    </div>
                </td>
            </tr>
        );
    };

    return (
        <>
            <ModalComponent IsOpen={isOpen} text={`Tem a certeza que quer remover a peça ${removePart?.name} da assistência com o id ${assistanceId}`} OnClickRight={() => setIsOpen(false)} OnClickLeft={() => { setIsOpen(false); Remove(removePart) }} title={"Remover peça da assistência"} buttonRight={"Não"} buttonLeft={"Sim"} />
            <Row style={{ margin: "1rem 0px" }}>
                {isMobile ? (
                    <>
                        <div className="parts-card-grid">
                            {isLoadingTable ? (
                                <LoadingSpinner />
                            ) : parts.length > 0 ? (
                                parts.map((part, index) => (
                                    <UsedPartCard
                                        key={index}
                                        part={part}
                                        onRemove={() => {
                                            setIsOpen(true);
                                            setRemovedPart(part);
                                        }}
                                        onUpdateBillable={(billable) => UpdateBillable(part, billable)}
                                        onBillableChange={(billable) => {
                                            setParts((prevParts) =>
                                                prevParts.map((prevPart) =>
                                                    prevPart.id === part.id
                                                        ? { ...prevPart, billable: billable }
                                                        : prevPart
                                                )
                                            );
                                        }}
                                        showBillable={isActiveProfabilityModule && preferedView === "Backoffice" && !isComplete}
                                        showUpdateButton={isActiveProfabilityModule && preferedView === "Backoffice" && !isComplete}
                                        showRemoveButton={(assistanceStatus.toUpperCase() === 'EM CURSO') && !isLocked}
                                        isLocked={isLocked}
                                    />
                                ))
                            ) : (
                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                    <FormattedMessage id="NoResults" />
                                </div>
                            )}
                        </div>
                        {totalPages > 1 && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '1rem 0.5rem',
                                opacity: isLoadingTable ? 0.5 : 1,
                                width: '100%'
                            }}>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={!canPreviousPage || isLoadingTable}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: canPreviousPage && !isLoadingTable ? '#007bff' : '#ccc',
                                        cursor: canPreviousPage && !isLoadingTable ? 'pointer' : 'not-allowed',
                                        fontSize: '18px',
                                        padding: '4px 8px'
                                    }}
                                >
                                    &lt;
                                </button>

                                <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>
                                    Página {currentPage} de {totalPages}
                                </span>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={!canNextPage || isLoadingTable}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: canNextPage && !isLoadingTable ? '#007bff' : '#ccc',
                                        cursor: canNextPage && !isLoadingTable ? 'pointer' : 'not-allowed',
                                        fontSize: '18px',
                                        padding: '4px 8px'
                                    }}
                                >
                                    &gt;
                                </button>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto', paddingLeft: '12px' }}>
                                    <span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>Por página:</span>
                                    <select
                                        disabled={isLoadingTable}
                                        value={pageSize}
                                        onChange={(e) => setPageSize(Number(e.target.value))}
                                        style={{
                                            padding: '4px 8px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            cursor: isLoadingTable ? 'not-allowed' : 'pointer',
                                            backgroundColor: 'white'
                                        }}
                                    >
                                        {pageSizes.map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <TableComponent
                        goToPage={goToPage}
                        loading={isLoadingTable}
                        pageCount={totalPages}
                        columns={columns.filter((column) => column.show !== false)} // Filtrar colunas --> algumas nao sao visiveis para os tecnicos
                        manualPagination={true}
                        fetchData={getUsedPartsOrchestrator}
                        pageSizes={pageSizes}
                        initialPage={currentPage}
                        initialPageSize={pageSize}
                        initialSortBy={[]}
                        data={parts}
                        dontOrderFirstCollumn={true}
                        disableSortBy={true}
                        renderRowSubComponent={renderRowSubComponent}
                        expandedRows={expandedRows}
                    />
                )}
            </Row>
        </>
    )
}
